<script setup lang="ts">
/**
 * A reminder for a skipped onboarding step (MAR-933) — a small bubble anchored beside the step's own
 * icon in the rail, with that icon ringed in ink (`IconRail`'s `ringed()`). No dimming, no focus trap: it is a nudge next to
 * where the thing lives, not a modal, and the page stays fully usable underneath it.
 *
 * A `ui/popover` anchored to that icon (MAR-948): portalled to `body`, because the rail clips its
 * overflow, and kept on screen by reka's popper instead of a hand-computed `top`/`left`. Because it
 * is a nudge, three of the popover's defaults are switched off: it does not take focus when it
 * appears, and neither Escape nor an outside press closes it — only its own two buttons do.
 *
 * Renders nothing while the anchor has no box — below `sm` the rail is hidden, and a bubble pointing
 * at nothing would be worse than none.
 */
import { Popover, PopoverAnchor, PopoverArrow, PopoverContent } from '~/components/ui/popover'

const props = defineProps<{
  anchor: HTMLElement | null
  title: string
  body: string
  actionLabel: string
}>()
const emit = defineEmits<{ act: []; dismiss: [] }>()

const { t } = useI18n()
const visible = ref(false)

function measure() {
  const r = props.anchor?.getBoundingClientRect()
  visible.value = !!r && !!r.width && !!r.height
}

let observer: ResizeObserver | null = null
watch(() => props.anchor, (el) => {
  observer?.disconnect()
  measure()
  if (el && typeof ResizeObserver !== 'undefined') {
    observer = new ResizeObserver(measure)
    observer.observe(el)
    observer.observe(document.body)
  }
}, { immediate: true, flush: 'post' })

onMounted(() => window.addEventListener('resize', measure))
onBeforeUnmount(() => {
  observer?.disconnect()
  window.removeEventListener('resize', measure)
})
</script>

<template>
  <Popover :open="visible && !!anchor">
    <PopoverAnchor v-if="anchor" :reference="anchor" />
    <PopoverContent
      side="right"
      align="start"
      :side-offset="12"
      :align-offset="-4"
      :aria-label="title"
      class="z-50 w-[300px] rounded-xl p-4"
      data-testid="onboarding-reminder"
      @open-auto-focus.prevent
      @close-auto-focus.prevent
      @escape-key-down.prevent
      @interact-outside.prevent
    >
      <PopoverArrow :width="12" :height="6" />
      <p class="text-sm font-semibold">{{ title }}</p>
      <p class="mt-1 text-[13px] leading-relaxed text-muted-foreground">{{ body }}</p>
      <!--
        44×44 targets for EVERY pointer (MAR-933 §20), not only under `pointer-coarse`. The primary
        button keeps the mockup's 36px pill: the button is the 44px hit area and the pill is the span
        inside it. "No mostrar más" has no fill, so its own box simply grows — nothing to see.
        `mt-2` rather than `mt-3`: the taller targets carry the rest of the spacing. On a touch screen
        (`pointer-coarse`) the pill is 44px and the gap `mt-3` (tester F1).
        The pill is an OUTLINE (`border-input`, ink text, `sidebar-hover` on hover), the same treatment
        as the rail's new-item buttons: the bubble sits beside the rail, where the active nav row must
        stay the only solid amber fill (MAR-1432 S1). It was a solid accent fill, a second amber.
      -->
      <div class="mt-2 pointer-coarse:mt-3 flex gap-2">
        <button
          type="button"
          class="group inline-flex min-h-11 min-w-11 items-center rounded-lg focus-visible:outline-none"
          data-testid="onboarding-reminder-act"
          @click="emit('act')"
        >
          <span class="inline-flex min-h-9 pointer-coarse:min-h-11 w-full items-center justify-center rounded-lg border border-input px-3.5 text-[13px] font-semibold text-foreground transition-colors group-hover:bg-sidebar-hover group-focus-visible:ring-2 group-focus-visible:ring-ring">
            {{ actionLabel }}
          </span>
        </button>
        <button
          type="button"
          class="min-h-11 min-w-11 rounded-lg px-3 text-[13px] text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid="onboarding-reminder-dismiss"
          @click="emit('dismiss')"
        >
          {{ t('onboarding.reminders.dismiss') }}
        </button>
      </div>
    </PopoverContent>
  </Popover>
</template>
